<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    validators: value => ['primary', 'secondary', 'tertiary'].includes(value),
    default: 'primary',
  },
  size: {
    type: String,
    validators: value => ['sm', 'md', 'lg'].includes(value),
    default: 'md',
  },
  text: {
    type: String,
    required: false,
  },
  icon: {
    type: String,
    required: false,
  },
  iconPosition: {
    type: String,
    validators: value => ['left', 'right'].includes(value),
    default: 'right',
  },
  iconPack: {
    type: String,
    default: 'linear',
    validators: value => ['linear', 'twotones', 'flags'].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const iconSize = computed(() => {
  if (props.icon) {
    return props.size === 'sm' ? 16 : props.size === 'md' ? 20 : 22
  }
  return false
})
</script>

<template>
  <button
    v-bind="$attrs"
    class="flex cursor-pointer items-center justify-center gap-2 rounded-lg"
    :class="{
      [variant]: variant && !loading,
      [`${variant}-loading`]: variant && loading,
      'text-xs': size === 'sm',
      'text-sm': size === 'md',
      'text-lg': size === 'lg',
      'flex-row': iconPosition === 'right',
      'flex-row-reverse': iconPosition === 'left' && !loading,
      'px-4 py-2': text,
      'p-2': !text,
      'p-2.5': icon && !text && size === 'lg',
      'border-none': !text && variant === 'tertiary',
    }"
  >
    <p v-if="text">{{ text }}</p>
    <ImoIcon v-if="icon && !loading" :size="iconSize" :name="icon" :pack="iconPack" />
    <ImoSpinner v-if="loading" :variant="variant" />
  </button>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.primary {
  @apply bg-primary-90 text-white hover:bg-primary-60 border-[0.1px] border-primary-90 hover:border-primary-60 disabled:bg-primary-0 disabled:border-primary-0 disabled:text-primary-30 disabled:pointer-events-none;
}

.secondary {
  @apply text-secondary-50 hover:bg-secondary-0 border-[0.1px] border-secondary-30 disabled:border-none disabled:text-secondary-10 disabled:pointer-events-none;
}

.tertiary {
  @apply text-neutral-60 hover:bg-neutral-20 border-[0.1px] border-neutral-30 hover:border-neutral-20 disabled:border-neutral-20 disabled:text-neutral-20 disabled:pointer-events-none;
}

.special {
  @apply text-info-80 hover:bg-info-20 border-[0.1px] border-info-80 hover:border-info-20 disabled:border-info-20 disabled:text-info-20 disabled:pointer-events-none;
}

.error {
  @apply bg-white text-error-70 hover:bg-error-20 border-[0.1px] border-error-70 hover:border-error-50 disabled:bg-primary-50 disabled:border-primary-50 disabled:text-primary-30 disabled:pointer-events-none;
}

/* Loading states for each variant */
.primary-loading {
  @apply bg-primary-30 text-white border-[0.1px] border-primary-30 pointer-events-none;
}

.secondary-loading {
  @apply bg-none text-secondary-30 border-transparent pointer-events-none;
}

.tertiary-loading {
  @apply bg-none text-neutral-40 border-[0.1px] border-neutral-30 pointer-events-none;
}

.special-loading {
  @apply bg-info-10 text-info-60 border-[0.1px] border-info-20 pointer-events-none;
}
</style>
